import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  TableCell,
  TableRow,
  Box,
  Checkbox,
  Collapse,
  TextareaAutosize,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomTypography from '../../ui/atoms/CustomTypography';
import IconImages from '../../assets/icons/IconImages';
import IconAcceptQuestion from '../../assets/icons/IconAcceptQuestion';
import KebabMenu from '../../ui/organisms/MenuItems/KebabMenu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CustomTooltip from '../../ui/atoms/CustomTooltip';
import IconClock from '../../assets/icons/IconClock';
import IconFileKebabMenu from '../../assets/icons/IconFileKebabMenu';
import {
  AuditStateQuestion,
  SaveDataAnswerGroup,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import QuestionItemView from './QuestionItemView';
import { useDispatch, useSelector } from 'react-redux';
import {
  getScriptSave,
  setAccept,
  setFollowup,
  setKeepExisting,
  getScriptState,
} from '../../redux/reducer/coreScriptReducer';
import { useVisualisationRequired } from '../../utils/helpers/formatValue';
import { RootState } from '../../redux/Store';
import QuestionGallery from './QuestionGallery';
import AiInformation from './AiInformation';

import { useAppSelector } from '../../redux/Store';
import { toggleQuestion } from '../../redux/reducer/selectedQuestionReducer';
import FlagIcon from './Meddalions';
import disableKeepExisting from '../../utils/helpers/useDisableCurrent';
import ErrorMessage from '../../ui/atoms/ErrorMessage';
import { renderFormattedAnswers } from '../../utils/helpers/RenderFormatAnswers';
import SendFollowup from './SendFollowup';
import ViewHistory from './ViewHistory';
import { Platform } from '../../constants/platform';
import { formatValue } from '../../utils/helpers/formatValue';

const Textarea = styled(TextareaAutosize)(
  () => `
  display: flex;
  height: 100px;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #E3E3E3;
  background: #FFF;
`
);

type ImageResult = {
  have: number;
  total: number;
};

type QuestionItemProps = {
  index: number;
  question: AuditStateQuestion;
  answers: SaveDataAnswerGroup | undefined;
  expanded?: boolean;
  title: string;
  aishow: boolean;

  keyToString: (key: string) => string;
  onSetAnswer?: (
    questionKey: string,
    comments: string,
    answers: string[]
  ) => void;
};
function norm(arr: string[]) {
  return arr.map((a) => (a ?? '').toLowerCase()).sort();
}
function arraysEqualIgnoreCase(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) return false;
  const n1 = norm(arr1);
  const n2 = norm(arr2);
  return n1.every((v, i) => v === n2[i]);
}

const Question = ({
  question,
  index,
  answers,
  keyToString,
  onSetAnswer,
  title,
  aishow = false,
}: QuestionItemProps) => {
  const saveData = useSelector(getScriptSave);
  const decisionData = useSelector(
    (state: RootState) => state.contextId.decisionData
  );
  const allSelected = useSelector(
    (state: RootState) => state.selectedQuestion.allSelected
  );
  const checkboxState = useSelector(
    (state: RootState) => state.selectedQuestion.stations
  );
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly || question.ReadOnly
  );

  const isDecisionTaken = Boolean(question?.AuditDecision);

  const isReadOnlyAccept = useSelector(
    (state: RootState) =>
      state.contextId.isReadOnly ||
      allSelected ||
      (isDecisionTaken && Number(question?.AuditDecision?.DecisionId) !== 3)
  );

  const dispatch = useDispatch();
  const script = useAppSelector(getScriptState);

  const imageResult: ImageResult = useMemo(() => {
    if (
      question?.Title &&
      script.Media &&
      script.Media.ByQuestion &&
      script.Media.List
    ) {
      const mediaKeys = script.Media.ByQuestion[question.Title] || [];
      const mediaItems = mediaKeys
        .map((key) => script.Media.List[key]) // Map the keys to media objects
        .filter(Boolean); // Filter out undefined items

      const total = mediaItems.length; // Total media items
      const have = mediaItems.filter((item) => item.Have > 0).length; // Media items with at least one

      return { have, total };
    }
    return { have: 0, total: 0 }; // Default values
  }, [question, script]);

  const formattedAnswers = useMemo(() => {
    const answers = saveData?.Answers[question?.Title]?.Answers || [];
    return renderFormattedAnswers(answers, question, true);
  }, [saveData?.Answers, question]);

  const formattedAiAnswers = useMemo(() => {
    const aianswers = question?.AiAudit?.Answers || [];
    return renderFormattedAnswers(aianswers, question);
  }, [question]);

  const getActiveAnswers = useMemo(() => {
    const allAnswers = saveData?.Answers[question?.Title]?.Answers || [];
    return allAnswers.filter((ans: any) => ans?.Active !== false);
  }, [question, saveData?.Answers]);

  const aiMatchStatus = useMemo(() => {
    const aianswers = (question?.AiAudit?.Answers || [])
      .flatMap((answerObj) => {
        const formattedValue = formatValue(question, answerObj);
        return formattedValue?.includes('~')
          ? formattedValue.split('~').map((part: string) => part.trim())
          : [formattedValue];
      })
      .filter(Boolean);

    if (aianswers.length === 0) return 'dot-hide';

    const answers = getActiveAnswers
      .map((a) => formatValue(question, a.Answer))
      .filter(Boolean);

    //at least one answer from ai so if no answers from user then red dot
    if (answers.length === 0) return 'dot-red';

    if (arraysEqualIgnoreCase(aianswers, answers)) return 'dot-green';
    return 'dot-red'; // Red dot
  }, [getActiveAnswers, question]);

  const [galleryExpended, setGalleryExpended] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false); // State for expanding the row
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false); // State for modal visibility
  const [localAnswers, setLocalAnswers] = useState<string[]>([]);

  const [comment, setComment] = useState('');
  const [hasAnswerChanged, setHasAnswerChanged] = useState(false); // Track changes in answers
  const maxCharLimit = 3800;

  const question_Title = keyToString(question?.Title);

  const undefinedMedallion = question?.Medallions === undefined;

  const notAnswered = (() => {
    return (
      getActiveAnswers.length === 0 ||
      (getActiveAnswers.length === 1 && !getActiveAnswers[0]?.Answer)
    );
  })();

  const isInvalidAnswer = (() => {
    if (!question?.Required) return false;
    return notAnswered;
  })();

  const showRedBox =
    question?.ErrorMessage ||
    (undefinedMedallion == false && isDecisionTaken == false) ||
    isInvalidAnswer;

  useEffect(() => {
    if (question?.AuditDecision?.Comments) {
      setComment(question?.AuditDecision?.Comments);
    }
    setIsExpanded(isInvalidAnswer || !!question?.ErrorMessage);
  }, [
    isInvalidAnswer,
    question?.AuditDecision?.Comments,
    question?.ErrorMessage,
  ]);

  // Add a derived state to determine if the save button should be disabled
  const isSaveDisabled = useMemo(() => {
    // Save button is disabled if there's an error message and no answer change
    return !!question?.ErrorMessage && !hasAnswerChanged;
  }, [question?.ErrorMessage, hasAnswerChanged]);

  const handleQuestionToggle = (
    station: string,
    questionKey: string,
    isChecked: boolean
  ) => {
    dispatch(toggleQuestion({ station, questionKey, isChecked }));
  };

  const handleCommentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    if (value.length <= maxCharLimit) {
      setComment(value);
    }
  };

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAnswerChange = (
    questionKey: string,
    updatedAnswers: string[]
  ) => {
    if (question?.Type === 'SingleSelect' && onSetAnswer) {
      onSetAnswer(questionKey, comment, updatedAnswers);
      setIsExpanded(false);
    }
    setLocalAnswers(updatedAnswers);
    setHasAnswerChanged(true); // Mark answers as changed
  };

  const handleKeepCurrent = () => {
    dispatch(
      setKeepExisting({
        questionPath: question?.QuestionKey,
        answers: question?.AuditCurrent || [],
      })
    );
  };

  // Trigger the onSetAnswer function when the Save button is clicked
  const handleSave = () => {
    if (onSetAnswer && question?.QuestionKey) {
      onSetAnswer(question.QuestionKey, comment, localAnswers);
      setHasAnswerChanged(false);
    }
  };

  const handleAccept = () => {
    dispatch(
      setAccept({
        questionPaths: [question?.QuestionKey],
      })
    );
  };

  const handleSendFollowUp = (selectedReason: any, comments: string) => {
    dispatch(
      setFollowup({
        questionPath: question?.QuestionKey,
        reasonid: Number(selectedReason?.id),
        comments: comments,
      })
    );
    handleModalClose();
  };

  const handleHistoryModalClose = () => {
    setIsHistoryModalOpen(false);
  };

  // Determine background color based on index
  const backgroundColor = index % 2 === 1 ? '#FBFBFB' : '#ffffff';

  const menuOptions = [
    {
      label: (
        <CustomTypography
          variant='body1'
          sx={{
            color: '#343330',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: 'normal',
            textTransform: 'capitalize',
          }}
        >
          Send Follow-Up
        </CustomTypography>
      ),
      onClick: handleModalOpen, // Open modal on click
      prefixIcon: <IconClock />,
    },
    !disableKeepExisting(question) &&
      question?.AuditCurrent &&
      question?.AuditCurrent?.length > 0 && {
        label: (
          <CustomTypography
            variant='body1'
            sx={{
              color: '#343330',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: 'normal',
              textTransform: 'capitalize',
            }}
          >
            Keep Current
          </CustomTypography>
        ),
        onClick: handleKeepCurrent,
        prefixIcon: <IconFileKebabMenu />,
      },
  ].filter(Boolean); // Remove falsey values

  const handleAnswersForNew = () => {
    if (saveData?.Answers[question?.Title]) {
      const tempAns = getActiveAnswers.map((answerObj) => answerObj.Answer);
      setLocalAnswers([...tempAns]);
    }
  };
  const handleCommentsForNew = () => {
    if (
      question?.Type === 'SingleSelect' &&
      onSetAnswer &&
      question?.QuestionKey
    ) {
      onSetAnswer(question.QuestionKey, comment, localAnswers);
      // setComment('');
      setIsExpanded(false);
    }
  };

  const checkForDisabling = () => {
    if (
      isDecisionTaken ||
      isReadOnly ||
      undefinedMedallion ||
      allSelected ||
      getActiveAnswers.every((answer: any) => answer?.Answer === '')
    )
      return true;

    return false;
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          borderBottom: showRedBox
            ? '1px solid rgba(243, 86, 37, 0.50)'
            : '1px solid rgba(0, 0, 0, 0.1)', // Default: No border
          borderTop: showRedBox
            ? '1px solid rgba(243, 86, 37, 0.50)'
            : '1px solid rgba(0, 0, 0,0.1)', // Default: No border
          borderLeft: showRedBox
            ? '1px solid rgba(243, 86, 37, 0.50)'
            : '1px solid rgba(0, 0, 0,0.1)', // Default: No border
          borderRight: showRedBox
            ? '1px solid rgba(243, 86, 37, 0.50)'
            : '1px solid rgba(0, 0, 0,0.1)', // Default: No border
          marginBottom: '10px', // Add whitespace between rows
          width: '100%',
        }}
      >
        <TableRow
          component='tr'
          sx={{
            display: 'flex',
            flex: 8,
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: backgroundColor,
          }}
          onClick={() => {
            setIsExpanded(!isExpanded);
            handleAnswersForNew();
          }}
        >
          {/* Checkbox Column */}
          <TableCell
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              border: 'none',
            }}
          >
            <Checkbox
              color='primary'
              sx={{
                width: '24px',
                height: '24px',
                '&.Mui-disabled': {
                  cursor: 'not-allowed',
                  pointerEvents: 'all !important', // Override pointer-events: none
                },
              }}
              disabled={checkForDisabling()}
              checked={!!checkboxState[title]?.includes(question.QuestionKey)}
              onChange={(e) =>
                handleQuestionToggle(
                  title,
                  question.QuestionKey,
                  e.target.checked
                )
              }
            />
          </TableCell>

          {/* Question Title Column */}
          <TableCell
            sx={{
              border: 'none',
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 500,
              flex: 1.5,
              lineHeight: '14.63px',
              textAlign: 'left', // Ensures text aligns left
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {question?.Required && (
              <p style={{ color: '#CB690E', margin: 0 }}>*</p>
            )}
            <CustomTypography
              variant='body1'
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                fontWeight: useVisualisationRequired(question) ? 600 : 500,
                lineHeight: '14.63px',
                color: '#343330',
                cursor: 'pointer',
                textAlign: 'left',
                textTransform: 'none',
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {/* {question_Title && question_Title.length > 20 ? ( */}
              <CustomTooltip
                label={keyToString(question_Title)}
                arrow
                placement='top'
              >
                {keyToString(question_Title)}
              </CustomTooltip>
              {/* // ) : (
            //   keyToString(question_Title)
            // )} */}
            </CustomTypography>
          </TableCell>

          {/* Answers Column */}
          <TableCell
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '14.63px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              flex: 1,
              border: 'none',
            }}
          >
            <Button
              variant='text'
              sx={{
                padding: 0, // Completely remove padding
                minWidth: 0, // Remove default button min-width
                display: 'flex', // Ensure flex layout
                alignItems: 'flex-start', // Align content to the start
                textAlign: 'left',
                textTransform: 'none',
                '&.Mui-disabled': {
                  cursor: 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              disabled={isReadOnly}
            >
              <CustomTypography
                variant='body1'
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  color: '#343330',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textTransform: 'none',
                  textDecorationLine: notAnswered ? 'none' : 'underline',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                <span
                  className={aiMatchStatus}
                  style={{ marginRight: 4 }}
                  role='img'
                  aria-label={
                    aiMatchStatus === 'dot-green'
                      ? 'AI answers match'
                      : aiMatchStatus === 'dot-red'
                        ? 'AI answers differ'
                        : ''
                  }
                />
                {formattedAnswers}
                {/* Hello World. This is a sample test for multiline ellipsis
              functionality. Ensure it works correctly for responsive designs.
              Keep text concise and responsive. */}
              </CustomTypography>
            </Button>
          </TableCell>

          {/* Audit Current Column */}
          <TableCell
            sx={{
              flex: 1,
              padding: '12px',
              border: 'none',
            }}
          >
            <Button
              variant='text'
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                '&.Mui-disabled': {
                  cursor: 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              onClick={(event) => {
                event.stopPropagation();
                setIsHistoryModalOpen(true);
              }}
              disabled={Platform?.isInspect()}
            >
              <CustomTypography
                variant='body1'
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  color: '#343330',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textTransform: 'none',
                  textDecorationLine:
                    question?.AuditCurrent?.length === 0 ||
                    !question?.AuditCurrent
                      ? 'none'
                      : 'underline',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {(() => {
                  const answers = question?.AuditCurrent || [];
                  return renderFormattedAnswers(answers, question);
                })()}
              </CustomTypography>
            </Button>
          </TableCell>

          {/* Ai Column */}

          {aishow && (
            <TableCell
              sx={{
                flex: 1,
                padding: '12px',
                border: 'none',
              }}
            >
              <CustomTypography
                variant='body1'
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  color: '#343330',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textTransform: 'none',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {formattedAiAnswers}
              </CustomTypography>
            </TableCell>
          )}

          {/* Image Result Column */}
          <TableCell
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              border: 'none',
              flex: 0.5,
              textAlign: 'left', // Align all content to the left
              display: 'flex', // Flexbox for alignment
              alignItems: 'center', // Vertically center
              gap: '4px', // Space between elements
            }}
          >
            {imageResult.total > 0 && (
              <Button
                sx={{
                  padding: 0, // Completely remove all padding
                  paddingTop: 0, // Set top padding to 0
                  paddingBottom: 0, // Set bottom padding to 0
                  paddingLeft: 0, // Set left padding to 0
                  paddingRight: 0, // Set right padding to 0
                  minWidth: 0, // Remove default min-width for buttons
                  display: 'flex', // Ensure horizontal alignment
                  alignItems: 'center', // Vertically align content within the button
                  textAlign: 'left', // Align button text to the left
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setGalleryExpended((prev) => !prev);
                }}
              >
                {/* <CustomTypography
                variant='body1'
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#CB690E',
                  textAlign: 'left', // Align the text to the left
                }}
              >
                *
              </CustomTypography> */}
                <IconImages />
                <CustomTypography
                  variant='body1'
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: '14.63px',
                    color: '#343330',
                    textAlign: 'left', // Align text to the left
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                    marginLeft: '4px', // Add spacing between the icon and text
                  }}
                >
                  {imageResult?.have}/{imageResult.total}
                </CustomTypography>
              </Button>
            )}
          </TableCell>

          {/* Decision Column */}
          <TableCell
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 500,
              flex: 2,
              lineHeight: '14.63px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              border: 'none',
              padding: '8px', // Reduce padding
              display: 'flex', // Ensure horizontal layout
              flexDirection: 'row', // Keep items in a row
              alignItems: 'center', // Vertically center content
              justifyContent: 'space-between', // Spread items across the row
              gap: '8px', // Consistent spacing between elements
            }}
          >
            {/* Decision Text */}
            <CustomTypography
              variant='body1'
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '14.63px',
                color: '#343330',
                textDecoration:
                  question?.AuditDecision?.DecisionId && 'underline',
                textAlign: 'start',
                whiteSpace: 'nowrap', // Prevent text wrapping
                overflow: 'hidden', // Handle overflow gracefully
                textOverflow: 'ellipsis', // Add ellipsis if text overflows
                flex: 1, // Allow text to take up available space
              }}
            >
              {isDecisionTaken ? (
                <>
                  {question?.AuditDecision?.DecisionId !== undefined &&
                    decisionData[question.AuditDecision.DecisionId]?.text}
                  {question?.AuditDecision?.Comments && (
                    <>
                      {' - '}
                      <CustomTooltip
                        label={question?.AuditDecision?.Comments} // Show full text in tooltip
                        arrow
                        placement='top'
                        tooltipWidth={1050}
                        tooltipHeight={290}
                      >
                        {/* Show the first 20 characters in the UI */}
                        {question?.AuditDecision?.Comments?.slice(0, 20)}...
                      </CustomTooltip>
                    </>
                  )}
                </>
              ) : (
                '-'
              )}
            </CustomTypography>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex', // Horizontal layout for buttons
                flexDirection: 'row', // Ensure buttons are in a row
                justifyContent: 'flex-end', // Align buttons to the right
                alignItems: 'center', // Vertically align buttons
                gap: '8px', // Consistent spacing between buttons
                flexWrap: 'wrap', // Allow wrapping for smaller screens
                '@media (max-width: 600px)': {
                  gap: '4px', // Reduce gap for smaller screens
                },
              }}
            >
              {/* Accept Button */}
              {!undefinedMedallion && (
                <Button
                  variant='text'
                  color='primary'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    minWidth: '64px', // Standard button size
                    padding: '4px 8px', // Adjust padding for compactness
                    '&.Mui-disabled': {
                      cursor: 'not-allowed',
                      pointerEvents: 'all !important',
                    },
                  }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleAccept();
                  }}
                  disabled={isReadOnlyAccept} // Updated logic
                >
                  <IconAcceptQuestion
                    color={
                      isDecisionTaken &&
                      Number(question?.AuditDecision?.DecisionId) !== 3
                        ? '#EAEAEA'
                        : '#1C6EC9'
                    }
                  />
                  <CustomTypography
                    variant='body1'
                    sx={{
                      color:
                        isDecisionTaken &&
                        Number(question?.AuditDecision?.DecisionId) !== 3
                          ? '#EAEAEA'
                          : '#1C6EC9',
                      fontFamily: 'Montserrat',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    Accept
                  </CustomTypography>
                </Button>
              )}

              {/* Flag Icon */}
              <FlagIcon
                isDecisionTaken={isDecisionTaken}
                medallions={question?.Medallions}
              />

              {/* Kebab Menu */}
              <KebabMenu
                disable={isReadOnly}
                options={menuOptions as any}
                icon={<MoreVertIcon />}
              />
            </Box>
          </TableCell>
        </TableRow>

        {question?.ErrorMessage && (
          <Box sx={{ display: 'flex', margin: '1rem' }}>
            <ErrorMessage>{keyToString(question?.ErrorMessage)}</ErrorMessage>
          </Box>
        )}
        <TableRow sx={{ display: 'flex', width: '100%' }}>
          <TableCell
            colSpan={6}
            sx={{
              border: 'none',
              padding: 0,
              display: 'flex',
              alignSelf: 'stretch',
              width: '100%',
              backgroundColor: backgroundColor,
            }}
          >
            <Collapse
              in={isExpanded}
              timeout='auto'
              unmountOnExit
              sx={{ display: 'flex', alignSelf: 'stretch', width: '100%' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignSelf: 'stretch',
                  width: '100%',
                  padding: '0px 72px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: '20px',
                  mt: '1.5rem',
                  backgroundColor: backgroundColor,
                }}
              >
                <Box
                  sx={{ display: 'flex', alignSelf: 'stretch', width: '50%' }}
                >
                  <QuestionItemView
                    disabled={isReadOnly}
                    question={question}
                    answers={answers}
                    keyToString={keyToString}
                    onSetAnswer={handleAnswerChange}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignSelf: 'stretch',
                    width: '50%',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <CustomTypography
                    variant='body1'
                    sx={{
                      paddingBottom: '6px',
                      alignItems: 'flex-start',
                      gap: '2px',
                      color: '#343330',
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '500',
                      lineHeight: 'normal',
                    }}
                  >
                    Comments
                  </CustomTypography>
                  <Textarea
                    disabled={isReadOnly}
                    minRows={3}
                    placeholder='Enter your comments here...'
                    onBlur={handleCommentsForNew}
                    value={comment}
                    sx={{
                      color: '#343330',
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '500',
                      lineHeight: '24px',
                      cursor: isReadOnly ? 'not-allowed' : null,
                    }}
                    onChange={handleCommentChange}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      width: '100%',
                      marginTop: '20px',
                    }}
                  >
                    <CustomTypography
                      variant='body1'
                      sx={{
                        color: '#9C9D9E',
                        textAlign: 'right',
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '18px',
                      }}
                    >
                      {comment.length}/{maxCharLimit} Characters
                    </CustomTypography>
                  </Box>
                </Box>
                {question?.Type !== 'SingleSelect' && (
                  <Box
                    sx={{
                      display: 'flex',
                      width: '50%',
                      height: '42px',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-start',
                      gap: '8px',
                    }}
                  >
                    <Button
                      variant='outlined'
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        width: '100px',
                        gap: '8px',
                        borderRadius: '48px',
                        border: '1px solid #E3E3E3',
                        background: '#FFF',
                        color: '#343330',
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '28px',
                      }}
                      onClick={() => {
                        setIsExpanded(!isExpanded);
                        setLocalAnswers([]);
                        // setComment('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      sx={{
                        display: 'flex',
                        height: '42px',
                        width: '100px',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        gap: '8px',
                        borderRadius: '48px',
                        border: '1px solid #E3E3E3',
                        background: '#1C6EC9',
                        color: '#FFF',
                        fontFamily: 'Montserrat',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: '500',
                        lineHeight: '28px',
                      }}
                      onClick={() => {
                        setIsExpanded(!isExpanded);
                        handleSave();
                        setLocalAnswers([]);
                        // setComment('');
                      }}
                      disabled={isReadOnly || isSaveDisabled}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
        {galleryExpended && (
          <QuestionGallery question={question?.Title} expanded={isExpanded} />
        )}

        {aishow && (
          <AiInformation
            question={question}
            expanded={aiMatchStatus === 'dot-red'}
          />
        )}
      </Box>

      <SendFollowup
        isModalOpen={isModalOpen}
        handleSendFollowUp={handleSendFollowUp}
        handleModalClose={handleModalClose}
        keyToString={keyToString}
        question_Title={question_Title}
        title={title}
        question={question}
      />
      {isHistoryModalOpen && (
        <ViewHistory
          isModalOpen={isHistoryModalOpen}
          handleModalClose={handleHistoryModalClose}
          question={question?.Title}
        />
      )}
    </React.Fragment>
  );
};

export default Question;
